import React, { useCallback, useRef } from "react";
import { Text, View, StyleSheet, ScrollView, Image } from "react-native";

import { AnalyticsService } from "../../constants/Analytics";

import OrderGroupCard from "../../components/order/OrderGroupCard";
import FontStyles from "../../constants/FontStyle";
import StyledButton from "../../components/StyledButton";
import Colors from "../../constants/Colors";
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import OrderRatingModal from "../../components/modals/OrderRatingModal";
import { OrderContext } from "../../contexts/order/Store";
import DeliverySummary from "../../components/common/DeliverySummary";
import InvoiceTable from "../../components/order/InvoiceTable";
import { IOrderGroup } from "../../interfaces/order";
import GenericHeader from "../../components/headers/GenericHeader";
import ViewShot from "react-native-view-shot";
import useOrderTotal from "../../hooks/useOrderTotal";
import { TransactionFeeNotice } from "../../components/common/TransactionFeeNotice";

const analytics = new AnalyticsService();

function OrderGroupInvoiceScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { state: order_state } = React.useContext(OrderContext);

  const mainRef = useRef(null);
  const [orderGroup, setOrderGroup] = React.useState<IOrderGroup>();
  const [isConfirmModalOpen, setConfirmModalOpen] =
    React.useState<boolean>(false);

  const hideConfirmModal = () => {
    setConfirmModalOpen(false);
  };
  const openConfirmModal = () => {
    setConfirmModalOpen(true);
  };

  const initialize = () => {
    if (
      route.params &&
      route.params?.order_id !== null &&
      order_state?.order_group?.length
    ) {
      let _order: IOrderGroup | undefined = order_state.order_group.find(
        (x: any) => x._id === route.params?.order_id
      );
      if (_order) {
        setOrderGroup({
          ..._order,
          is_latest: order_state.latest_order?._id === _order?._id,
        });
        navigation.setOptions({
          title: `Invoice #${_order.ref} - Suplias Now`,
        });
      } else {
        console.log("Order group not found.");
      }
    } else {
      console.log("Cannot read order group details.");
    }
  };

  const totalCharge = React.useMemo<any>(() => {
    if (orderGroup) {
      const { totalCharge: charge } = useOrderTotal(orderGroup, false);
      return charge;
    }
    return {};
  }, [orderGroup]);

  useFocusEffect(
    useCallback(() => {
      if (route.name === "OrderInvoice") {
        if (route.params && route.params.order_id !== null) {
          initialize();
        } else {
          navigation.navigate("OrderList");
        }
      } else {
        navigation.navigate("OrderList");
      }
      return () => {
        setOrderGroup(null);
      };
    }, [order_state, route])
  );

  return (
    <>
      {isConfirmModalOpen ? (
        <OrderRatingModal
          modalState={isConfirmModalOpen}
          hideModal={hideConfirmModal}
          order={orderGroup}
        />
      ) : null}

      <GenericHeader
        title={`Invoice ${orderGroup ? "#" + orderGroup?.ref : "..."}`}
        backAction={() => {
          navigation.navigate("OrderList");
        }}
      />

      {orderGroup ? (
        <ScrollView
          // collapsable={false}
          contentContainerStyle={styles.scrollContainer}
        >
          {totalCharge?.value > 0 && orderGroup?.tag === "pending" && (
            <TransactionFeeNotice amount={totalCharge?.value} />
          )}
          <ViewShot style={styles.container} ref={mainRef}>
            <OrderGroupCard
              order={orderGroup}
              readOnly={true}
              callRep={true}
              showAddress
              confirmAction={openConfirmModal}
              screenCaptureRef={mainRef}
            />

            <InvoiceTable orderGroup={orderGroup} />

            <View style={styles.deliverySummaryContainer}>
              <DeliverySummary isCart={false} order_group={orderGroup} />
            </View>
          </ViewShot>
        </ScrollView>
      ) : orderGroup === null ? null : (
        <ScrollView contentContainerStyle={styles.emptyContainer}>
          <Image
            source={require("../../assets/images/main/folder-grey.png")}
            style={styles.emptyImage}
          />
          <Text style={styles.emptyText}>
            Order details currently not available.
          </Text>
          <StyledButton
            label="View Orders"
            action={() => {
              navigation.navigate("OrderList");
            }}
            style={{
              marginTop: 32,
              width: "auto",
            }}
          />
        </ScrollView>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    justifyContent: "flex-start",
    backgroundColor: Colors.shades._5,
  },
  container: {
    // justifyContent: "flex-start",
    backgroundColor: Colors.shades._5,
    paddingBottom: 100,
  },
  deliverySummaryContainer: {
    backgroundColor: Colors.whiteColor,
    marginTop: 6,
    paddingBottom: 20,
  },
  emptyContainer: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    backgroundColor: Colors.whiteColor,
  },
  emptyImage: {
    width: 50,
    height: 40,
  },
  emptyText: {
    maxWidth: 215,
    ...FontStyles.normal,
    ...FontStyles.size14,
    ...FontStyles.lh20,
    ...FontStyles.gray,
    textAlign: "center",
    marginTop: 18,
  },
});

export default OrderGroupInvoiceScreen;
